import { useState, useCallback, useMemo } from 'react';
import { useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Typography,
  IconButton,
  FormHelperText,
  Box,
  Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import { type LocationRecord } from './LocationImporterView';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';
import { LocationMapPicker } from './LocationMapPicker';
import {
  LocationBoundaryDrawer,
  type LocationBoundaryResult,
} from './LocationBoundaryDrawer';

const LOCATION_TYPES = [
  'Loading',
  'Dumping',
  'Parking',
  'Workshop',
  'Fueling',
  'Weighbridge',
  'Crushing',
  'Processing',
  'Stockpile',
  'Other',
];

interface FormData {
  name: string;
  locationType: string;
  easting: string;
  northing: string;
  elevation: string;
  trucksCapacity: string;
  minutes: string;
}

interface FormErrors {
  name?: string;
  locationType?: string;
  easting?: string;
  northing?: string;
  elevation?: string;
  trucksCapacity?: string;
  minutes?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onAdd: (location: LocationRecord) => void;
  locations?: LocationRecord[];
}

const DEFAULT_FORM: FormData = {
  name: '',
  locationType: '',
  easting: '',
  northing: '',
  elevation: '387',
  trucksCapacity: '',
  minutes: '2',
};

export const AddLocationModal = ({
  open,
  onClose,
  onAdd,
  locations,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const { proj4String, localOriginEasting, localOriginNorthing } =
    useAppSelector((s) => s.persistedReducer.customer);
  const [form, setForm] = useState<FormData>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showMap, setShowMap] = useState(false);
  const [pickedLatLon, setPickedLatLon] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  const [step, setStep] = useState<'form' | 'draw'>('form');
  const [pendingLocation, setPendingLocation] = useState<LocationRecord | null>(
    null
  );

  const validate = (): boolean => {
    const next: FormErrors = {};
    if (!form.name.trim()) next.name = t('loc_importer_err_required');
    if (!form.locationType) next.locationType = t('loc_importer_err_required');
    if (!form.easting.trim()) {
      next.easting = t('loc_importer_err_required');
    } else if (isNaN(parseFloat(form.easting))) {
      next.easting = t('loc_importer_err_number');
    }
    if (!form.northing.trim()) {
      next.northing = t('loc_importer_err_required');
    } else if (isNaN(parseFloat(form.northing))) {
      next.northing = t('loc_importer_err_number');
    }
    if (!form.elevation.trim()) {
      next.elevation = t('loc_importer_err_required');
    } else if (isNaN(parseFloat(form.elevation))) {
      next.elevation = t('loc_importer_err_number');
    }
    if (
      form.trucksCapacity.trim() &&
      isNaN(parseInt(form.trucksCapacity, 10))
    ) {
      next.trucksCapacity = t('loc_importer_err_number');
    }
    if (form.minutes.trim() && isNaN(parseInt(form.minutes, 10))) {
      next.minutes = t('loc_importer_err_number');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    setForm(DEFAULT_FORM);
    setErrors({});
    setShowMap(false);
    setPickedLatLon(null);
    setStep('form');
    setPendingLocation(null);
    onClose();
  };

  const handleMapPick = useCallback(
    (easting: number, northing: number, lat: number, lon: number) => {
      setForm((prev) => ({
        ...prev,
        easting: String(easting),
        northing: String(northing),
      }));
      setPickedLatLon({ lat, lon });
      setErrors((prev) => ({
        ...prev,
        easting: undefined,
        northing: undefined,
      }));
    },
    []
  );

  const handleSubmit = () => {
    if (!validate()) return;
    const location: LocationRecord = {
      id: crypto.randomUUID(),
      name: form.name.trim(),
      locationType: form.locationType,
      easting: parseFloat(form.easting),
      northing: parseFloat(form.northing),
      elevation: parseFloat(form.elevation),
      active: true,
      ...(form.trucksCapacity.trim() && {
        trucksCapacity: parseInt(form.trucksCapacity, 10),
      }),
      ...(form.minutes.trim() && {
        minutes: parseInt(form.minutes, 10),
      }),
      ...(pickedLatLon && {
        centroidLat: pickedLatLon.lat,
        centroidLon: pickedLatLon.lon,
      }),
    };
    setPendingLocation(location);
    setStep('draw');
  };

  const handleBoundarySave = (result: LocationBoundaryResult) => {
    if (!pendingLocation) return;
    onAdd({
      ...pendingLocation,
      zoneGeog: result.zoneGeog,
    });
    handleClose();
  };

  const field =
    (key: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [key]: e.target.value }));
    };

  const otherLocationsForMap = useMemo(
    () =>
      locations?.map((l) => ({
        id: l.id,
        name: l.name,
        locationType: l.locationType,
        centroidLat: l.centroidLat,
        centroidLon: l.centroidLon,
        geohashes: l.geohashes,
        zoneGeog: l.zoneGeog,
      })),
    [locations]
  );

  const palette = clientThemePalette[CLIENT_THEME].light;

  return (
    <>
      <Dialog
        open={open && step === 'form'}
        onClose={handleClose}
        maxWidth={showMap ? 'md' : 'sm'}
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: (t) => alpha(t.palette.primary.main, 0.15),
            backgroundImage: 'none',
          },
        }}
      >
        {/* ── Header ── */}
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            pt: 2.5,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.12),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.primary.main, 0.25),
              }}
            >
              <FmdGoodOutlinedIcon
                sx={{ fontSize: 18, color: 'primary.main' }}
              />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
                {t('loc_importer_add_title')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('loc_importer_add_subtitle')}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        {/* ── Location Info ── */}
        <Box
          sx={{
            mx: 3,
            mb: 1,
            pb: 0.5,
            borderBottom: '1px solid',
            borderColor: (t) => alpha(t.palette.divider, 0.6),
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
          >
            {t('loc_importer_section_info')}
          </Typography>
        </Box>

        <DialogContent sx={{ px: 3, pt: 1.5, pb: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('loc_importer_field_name')}
                fullWidth
                size="small"
                value={form.name}
                onChange={(e) => {
                  setForm((prev) => ({
                    ...prev,
                    name: e.target.value.replace(/\s/g, ''),
                  }));
                }}
                error={!!errors.name}
                helperText={errors.name}
                inputProps={{ maxLength: 255 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" error={!!errors.locationType}>
                <InputLabel>{t('loc_importer_field_type')}</InputLabel>
                <Select
                  label={t('loc_importer_field_type')}
                  value={form.locationType}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      locationType: e.target.value,
                    }));
                  }}
                >
                  {LOCATION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
                {errors.locationType && (
                  <FormHelperText>{errors.locationType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('loc_importer_field_trucks_capacity')}
                fullWidth
                size="small"
                type="number"
                value={form.trucksCapacity}
                onChange={field('trucksCapacity')}
                error={!!errors.trucksCapacity}
                helperText={errors.trucksCapacity}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label={t('loc_importer_field_minutes')}
                fullWidth
                size="small"
                type="number"
                value={form.minutes}
                onChange={field('minutes')}
                error={!!errors.minutes}
                helperText={errors.minutes}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        {/* ── Coordinates ── */}
        <Box
          sx={{
            mx: 3,
            mt: 2,
            mb: 1,
            pb: 0.5,
            borderBottom: '1px solid',
            borderColor: (t) => alpha(t.palette.divider, 0.6),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="caption"
            fontWeight={700}
            color="text.secondary"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}
          >
            {t('loc_importer_section_coords')}
          </Typography>
          <Button
            size="small"
            startIcon={<MapOutlinedIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setShowMap((v) => !v);
            }}
            sx={{
              fontSize: '0.72rem',
              fontWeight: 600,
              borderRadius: '6px',
              color: showMap ? 'primary.main' : 'text.secondary',
              bgcolor: (th) =>
                showMap ? alpha(th.palette.primary.main, 0.1) : 'transparent',
              '&:hover': {
                bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              },
              px: 1,
              py: 0.3,
              minWidth: 0,
            }}
          >
            {showMap ? t('loc_importer_map_hide') : t('loc_importer_map_pick')}
          </Button>
        </Box>

        <DialogContent sx={{ px: 3, pt: 1.5, pb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label={t('loc_importer_field_easting')}
                fullWidth
                size="small"
                type="number"
                value={form.easting}
                onChange={field('easting')}
                error={!!errors.easting}
                helperText={errors.easting}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label={t('loc_importer_field_northing')}
                fullWidth
                size="small"
                type="number"
                value={form.northing}
                onChange={field('northing')}
                error={!!errors.northing}
                helperText={errors.northing}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label={t('loc_importer_field_elevation')}
                fullWidth
                size="small"
                type="number"
                value={form.elevation}
                onChange={field('elevation')}
                error={!!errors.elevation}
                helperText={errors.elevation}
              />
            </Grid>
          </Grid>
        </DialogContent>

        {/* ── Map Picker ── */}
        <Collapse in={showMap} unmountOnExit>
          <Box sx={{ px: 3, pb: 2 }}>
            <LocationMapPicker
              onPick={handleMapPick}
              proj4String={proj4String}
              localOriginEasting={localOriginEasting}
              localOriginNorthing={localOriginNorthing}
              initialEasting={
                form.easting ? parseFloat(form.easting) : undefined
              }
              initialNorthing={
                form.northing ? parseFloat(form.northing) : undefined
              }
              otherLocations={otherLocationsForMap}
            />
          </Box>
        </Collapse>

        {/* ── Actions ── */}
        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: '1px solid',
            borderColor: (t) => alpha(t.palette.divider, 0.5),
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleClose}
            sx={{ borderRadius: '8px', fontWeight: 600 }}
          >
            {t('loc_importer_cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
          >
            {t('loc_importer_continue')}
          </Button>
        </DialogActions>
      </Dialog>

      <LocationBoundaryDrawer
        open={open && step === 'draw'}
        initialCenter={pickedLatLon ?? undefined}
        otherLocations={otherLocationsForMap}
        onSave={handleBoundarySave}
        onBack={() => {
          setStep('form');
        }}
      />
    </>
  );
};
